/**
 * Raid Path Core - pure grid pathfinding shared by the game and simulator.
 *
 * Extracted from the simulator's battle-tested player model for the
 * Auto-Scout Command upgrade: BFS start→exit maximizing (in order)
 * character skill match, special-room placement (water before campsite,
 * both near mid-path), encounter count, then path length. Encounter rooms
 * (gate/hazard) are capped at rows+cols-1; traversal rooms are free.
 */

/**
 * Score special-room placement: water before campsite, both near the middle.
 */
export function scoreSpecialRooms(path) {
  let waterIndex = -1;
  let campsiteIndex = -1;

  for (let i = 0; i < path.length; i++) {
    if (path[i].room?.type === 'water' && waterIndex === -1) {
      waterIndex = i;
    }
    if (path[i].room?.type === 'campsite' && campsiteIndex === -1) {
      campsiteIndex = i;
    }
  }

  const pathLength = path.length;
  const midpoint = pathLength / 2;
  const tolerance = pathLength * 0.25;
  let score = 0;

  // Each special room is a free benefit (fishing / healing) worth grabbing
  // whenever it adds no encounters. Keep every contribution strictly
  // NON-NEGATIVE so the pathfinder never rejects a free detour: the old
  // wrong-order penalty (-500) could make a free water+campsite detour score
  // worse than skipping it, so auto-scout would walk past free healing.
  if (waterIndex !== -1) {
    score += 500 + Math.max(0, tolerance - Math.abs(waterIndex - midpoint)) * 10;
  }

  if (campsiteIndex !== -1) {
    score += 500 + Math.max(0, tolerance - Math.abs(campsiteIndex - midpoint)) * 10;
  }

  // Prefer drinking (water) before resting (campsite) when both are present,
  // but only as a small tie-breaker between paths that already grab both -
  // never a penalty that would reject a free room.
  if (waterIndex !== -1 && campsiteIndex !== -1 && waterIndex < campsiteIndex) {
    score += 100;
  }

  return score;
}

/**
 * Score the encounter rooms on a path by whether the team can actually pass
 * them, rather than by how many there are.
 *
 * Encounters are where loot comes from, so the pathfinder should still want
 * them — but an encounter the team cannot pass is not loot, it is damage.
 * The old ordering preferred a higher raw encounter count unconditionally,
 * which is why a gate-heavy draw swung first-tour success from 90% down to
 * 40% with no way for the player to avoid it.
 *
 * A gate is roughly twice the risk of a hazard on two independent axes: it
 * fires 2–4 skill events where a hazard fires 1–2, and it sets a higher skill
 * bar (T1: 5 vs 3). Both are folded into `exposure`.
 *
 * @param {Array} path
 * @param {Object} characterSkills
 * @param {{gate: number, hazard: number}} skillTargets - tier skill bars
 * @returns {number} higher is better; negative means the path is over the
 *   team's head
 */
export function encounterValueForRoom(room, characterSkills, skillTargets) {
  const type = room?.type;
  if (type !== 'gate' && type !== 'hazard') return 0;

  // Without skill targets every encounter is worth exactly 1, which makes the
  // running score identical to the old raw encounter count - so callers that
  // do not opt in get byte-for-byte the previous pathfinding.
  if (!skillTargets) return 1;

  // Expected skill events per room (gate 2-4, hazard 1-2).
  const exposure = type === 'gate' ? 3 : 1.5;
  const target = type === 'gate' ? skillTargets.gate : skillTargets.hazard;
  const skill = characterSkills?.[room.primary] || 0;
  const deficit = Math.max(0, target - skill);

  // At or above the bar the room is worth taking: it is loot the team can
  // reach. Below it, each missing level costs ~5% per event, so the penalty
  // scales with both the shortfall and the number of rolls.
  return deficit === 0 ? exposure : -exposure * deficit;
}

/** Whole-path form of encounterValueForRoom (used by tests and callers). */
export function scoreEncounterValue(path, characterSkills, skillTargets) {
  let score = 0;
  for (const step of path) {
    score += encounterValueForRoom(step.room, characterSkills, skillTargets);
  }
  return score;
}

/**
 * Score a path by how well it matches the character's trained skills:
 * primary-skill rooms dominate, support skills add a smaller bonus.
 */
export function scoreCharacterSkillMatch(path, characterSkills) {
  if (!characterSkills) return 0;

  let score = 0;

  for (const step of path) {
    if (!step.room || step.room.type === 'blocked') continue;
    if (!step.room.primary) continue;

    const primarySkillLevel = characterSkills[step.room.primary] || 0;
    if (primarySkillLevel > 0) {
      score += 100 + (primarySkillLevel * 10);
    }

    if (step.room.support && Array.isArray(step.room.support)) {
      for (const supportSkill of step.room.support) {
        const supportSkillLevel = characterSkills[supportSkill] || 0;
        if (supportSkillLevel > 0) {
          score += 30 + (supportSkillLevel * 5);
        }
      }
    }
  }

  return score;
}

/**
 * Score how many wanted-loot rooms a path visits. wantedCells is a Set of
 * "row,col" keys for rooms that can drop items the player currently needs
 * (see the simulator's crafting-target model); null/empty means no targeting.
 */
export function scoreWantedLoot(path, wantedCells) {
  if (!wantedCells || wantedCells.size === 0) return 0;
  let score = 0;
  for (const step of path) {
    if (wantedCells.has(`${step.row},${step.col}`)) score++;
  }
  return score;
}

const TRAVERSAL_TYPES = new Set(['road', 'water', 'campsite', 'start', 'exit']);
const isSpecialType = (t) => t === 'water' || t === 'campsite';
const isEncounterType = (t) => t === 'gate' || t === 'hazard';

/**
 * Shortest free "leg" from A to B through unused traversal cells (road / water /
 * campsite - never gate/hazard) that passes through at least one special room.
 * Returns the intermediate cells [{row,col,room}] or null. `blocked` is the set
 * of "row,col" keys the leg may not step on (committed path cells).
 */
function findFreeSpecialLeg(grid, A, B, blocked, maxLen) {
  const rows = grid.rows, cols = grid.cols;
  const dirs = [[-1, 0], [1, 0], [0, -1], [0, 1]];
  const seen = new Set([`${A.row},${A.col},0`]);
  const queue = [{ row: A.row, col: A.col, specials: 0, cells: [] }];
  let best = null; // legs reaching B: keep the one grabbing the MOST specials (then shortest)
  while (queue.length > 0) {
    const cur = queue.shift();
    if (cur.cells.length >= maxLen) continue;
    for (const [dr, dc] of dirs) {
      const nr = cur.row + dr, nc = cur.col + dc;
      if (nr < 0 || nc < 0 || nr >= rows || nc >= cols) continue;
      const room = grid.grid[nr]?.[nc];
      if (!room) continue;
      if (nr === B.row && nc === B.col) {
        if (cur.specials > 0 && cur.cells.length > 0 &&
            (!best || cur.specials > best.specials ||
             (cur.specials === best.specials && cur.cells.length < best.cells.length))) {
          best = { cells: cur.cells, specials: cur.specials };
        }
        continue; // reached B - don't route through it
      }
      if (blocked.has(`${nr},${nc}`)) continue;
      if (!TRAVERSAL_TYPES.has(room.type)) continue; // never route a detour through an encounter
      if (cur.cells.some(c => c.row === nr && c.col === nc)) continue; // no intra-leg revisits
      const specials = cur.specials + (isSpecialType(room.type) ? 1 : 0);
      const key = `${nr},${nc},${specials}`;
      if (seen.has(key)) continue;
      seen.add(key);
      queue.push({ row: nr, col: nc, specials, cells: [...cur.cells, { row: nr, col: nc, room }] });
    }
  }
  return best ? best.cells : null;
}

/**
 * Post-process a chosen path to grab FREE water/campsite rooms: between two
 * consecutive anchors (start / exit / encounter) the path is only traversal
 * rooms, so that stretch can be rerouted through an adjacent water/campsite for
 * free healing/fishing - no encounter is added or dropped and the special rooms
 * cost no path-length budget. The BFS's length-maximizing dedup prunes these
 * detours in dense grids, so we re-insert them here rather than relying on the
 * pathfinder to surface them.
 */
export function insertFreeSpecialDetours(path, grid) {
  if (!path || path.length < 3 || !grid) return path;

  // Anchors are the rooms we must keep in order: start, exit, every encounter.
  const anchorIdx = [];
  for (let i = 0; i < path.length; i++) {
    if (i === 0 || i === path.length - 1 || isEncounterType(path[i].room?.type)) anchorIdx.push(i);
  }
  if (anchorIdx.length < 2) return path;

  // All original cells are reserved; each segment frees only its OWN traversal
  // cells before rerouting, so a detour can reuse this segment's roads but never
  // steal another segment's cells.
  const committed = new Set(path.map(s => `${s.row},${s.col}`));
  const result = [path[anchorIdx[0]]];

  for (let a = 0; a < anchorIdx.length - 1; a++) {
    const startI = anchorIdx[a], endI = anchorIdx[a + 1];
    const A = path[startI], B = path[endI];
    for (let k = startI + 1; k < endI; k++) committed.delete(`${path[k].row},${path[k].col}`);

    const segLen = endI - startI - 1;
    // Allow the reroute to run up to 6 rooms longer than the straight stretch:
    // enough to thread BOTH the water and the adjacent campsite (a 2-cell pair)
    // plus a few connector roads, without wandering far for a distant special.
    const leg = findFreeSpecialLeg(grid, A, B, committed, segLen + 6);
    if (leg) {
      for (const c of leg) { result.push(c); committed.add(`${c.row},${c.col}`); }
    } else {
      for (let k = startI + 1; k < endI; k++) { result.push(path[k]); committed.add(`${path[k].row},${path[k].col}`); }
    }
    result.push(B);
  }
  return result;
}

/**
 * BFS start→exit over non-blocked cells. Returns the best step array
 * [{row, col, room}, ...] including start and exit, or [] if unreachable.
 * Optional wantedCells (Set of "row,col") biases the choice toward rooms
 * that drop needed items — it dominates the other criteria, modeling a
 * player routing toward the loot they're farming. Callers that omit it
 * (the game's Auto-Scout) get the previous behavior unchanged.
 */
/**
 * @param {Object} currentRaidGrid
 * @param {Object} startPos
 * @param {Object} endPos
 * @param {Object} characterSkills
 * @param {Set|null} wantedCells
 * @param {{gate: number, hazard: number}|null} skillTargets - the tier's skill
 *   bars. When supplied, encounter rooms are ranked by whether the team can
 *   pass them instead of by raw count; omit to keep the pre-risk behaviour.
 */
export function findPathBFS(currentRaidGrid, startPos, endPos, characterSkills, wantedCells = null, skillTargets = null) {
  const rows = currentRaidGrid.rows;
  const cols = currentRaidGrid.cols;

  // Max encounters based on grid dimensions (only gate/hazard count)
  const maxPathLength = (rows || currentRaidGrid.size) + (cols || currentRaidGrid.size) - 1;
  const countsTowardLimit = new Set(['gate', 'hazard']);

  const startStep = {
    row: startPos.row,
    col: startPos.col,
    room: currentRaidGrid.grid[startPos.row][startPos.col]
  };

  const visited = new Map(); // "row,col" -> { path, encounterCount, pathLength }

  const queue = [{
    pos: startPos,
    path: [startStep],
    encounterCount: (countsTowardLimit.has(startStep.room?.type) ? 1 : 0),
    // Running risk-weighted value of the encounters taken so far. Carried
    // incrementally (rather than rescored per candidate) so it can drive the
    // visited-pruning below without an O(path) cost at every node.
    encounterScore: encounterValueForRoom(startStep.room, characterSkills, skillTargets)
  }];

  let bestExitPath = null;
  let bestEncounterScore = -Infinity;
  let bestPathLength = -1;
  let bestSpecialRoomsScore = -Infinity;
  let bestSkillMatchScore = -Infinity;
  let bestWantedScore = -Infinity;

  while (queue.length > 0) {
    const { pos, path, encounterCount, encounterScore } = queue.shift();
    const cellKey = `${pos.row},${pos.col}`;

    if (pos.row === endPos.row && pos.col === endPos.col) {
      const wantedScore = scoreWantedLoot(path, wantedCells);
      const specialRoomsScore = scoreSpecialRooms(path);
      const skillMatchScore = scoreCharacterSkillMatch(path, characterSkills);
      // Lexicographic: wanted loot > skill match > special rooms > encounters > length
      const candidate = [wantedScore, skillMatchScore, specialRoomsScore, encounterScore, path.length];
      const best = [bestWantedScore, bestSkillMatchScore, bestSpecialRoomsScore, bestEncounterScore, bestPathLength];
      const better = candidate.findIndex((v, i) => v !== best[i]);
      if (better !== -1 && candidate[better] > best[better]) {
        bestWantedScore = wantedScore;
        bestSkillMatchScore = skillMatchScore;
        bestSpecialRoomsScore = specialRoomsScore;
        bestEncounterScore = encounterScore;
        bestPathLength = path.length;
        bestExitPath = path;
      }
      continue;
    }

    // Keep the best-VALUED route to each cell, not the one with the most
    // encounter rooms. These are the same thing without skillTargets (every
    // encounter scores 1), but once rooms are risk-weighted a safer route must
    // be able to survive here - pruning on raw count discarded it before the
    // ranking below ever saw it, which is why weighting alone changed nothing.
    if (visited.has(cellKey)) {
      const prev = visited.get(cellKey);
      if (encounterScore < prev.encounterScore ||
          (encounterScore === prev.encounterScore && path.length <= prev.pathLength)) {
        continue;
      }
    }
    visited.set(cellKey, { path, encounterCount, encounterScore, pathLength: path.length });

    if (encounterCount > maxPathLength) continue;

    const directions = [
      { row: -1, col: 0 },
      { row: 1, col: 0 },
      { row: 0, col: -1 },
      { row: 0, col: 1 }
    ];

    for (const dir of directions) {
      const newRow = pos.row + dir.row;
      const newCol = pos.col + dir.col;

      if (newRow < 0 || newRow >= rows || newCol < 0 || newCol >= cols) continue;
      if (path.some(p => p.row === newRow && p.col === newCol)) continue;

      const room = currentRaidGrid.grid[newRow][newCol];
      if (!room || room.type === 'blocked') continue;

      const newEncounterCount = countsTowardLimit.has(room.type) ?
        encounterCount + 1 : encounterCount;
      if (newEncounterCount > maxPathLength) continue;

      queue.push({
        pos: { row: newRow, col: newCol },
        path: [...path, { row: newRow, col: newCol, room }],
        encounterCount: newEncounterCount,
        encounterScore: encounterScore + encounterValueForRoom(room, characterSkills, skillTargets)
      });
    }
  }

  return bestExitPath || [];
}
